import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useState } from "react";

import { ECONTRACT_STATUS } from "../../../enums";
import Payment from "../../../pages/contract/contractDetails/payment";
import { ESenderRole } from "../../../setup/api/services/contract/models";
import { checkFeedBack } from "../../../setup/api/services/feedbackAndReview";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import { FeedbackAndReview } from "./buyer/FeedbackAndReview/FeedbackAndReview";
import { WaitingForDeliveryActions } from "./escalation/waitingForDeliveryActions";
import { WaitingForInspectionActions } from "./escalation/waitingForInspectionActions";
import { EscalationFollowUpActions } from "./escalationFollowUp/escalationFollowUpActions";
import { IContractActionComponentProps } from "./models";
import { ActiveContractActions } from "./seller/activeContractActions";

const ContractActions = (props: IContractActionComponentProps) => {
  const { contract, actorType, refreshContractDetails } = props;
  const [isViewButtonFeedback, setIsViewButtonFeedback] = useState<boolean>();
  const [openFeedbackAfterInspection, setOpenFeedbackAfterInspection] =
    useState<boolean>(false);

  const renderSellerAction = () => {
    switch (contract.status) {
      case ECONTRACT_STATUS.CREATED:
        return (
          <ActiveContractActions
            contract={contract}
            actorType={actorType}
            refreshContractDetails={refreshContractDetails}
          />
        );
      case ECONTRACT_STATUS.WAITING_FOR_DELIVERY:
        return (
          <WaitingForDeliveryActions
            actorType={actorType}
            contract={contract}
            refreshContractDetails={refreshContractDetails}
          />
        );
      case ECONTRACT_STATUS.ITEM_INSPECTION:
        return (
          <WaitingForInspectionActions
            actorType={actorType}
            contract={contract}
            refreshContractDetails={refreshContractDetails}
          />
        );
      case ECONTRACT_STATUS.DELIVERY_ESCALATION:
        return <EscalationFollowUpActions />;
      case ECONTRACT_STATUS.INSPECTION_ESCALATION:
        return <EscalationFollowUpActions />;
      default:
        return null;
    }
  };
  const userId = AuthConfig.getUserInfo()?.id;
  const canProvideFeedBack = useCallback(() => {
    return (
      contract.status === ECONTRACT_STATUS.COMPLETED ||
      contract.status === ECONTRACT_STATUS.ITEM_ACCEPTED
    );
  }, [contract.status]);

  const toggleOpenFeedbackAfterInspection = useCallback(() => {
    setOpenFeedbackAfterInspection(!openFeedbackAfterInspection);
  }, [openFeedbackAfterInspection]);

  const closeViewButtonFeedback = useCallback(() => {
    setIsViewButtonFeedback(false);
  }, []);

  const fetchFeedBack = useCallback(
    async (contractId) => {
      try {
        const respons = await checkFeedBack(userId, contractId);
        setIsViewButtonFeedback(respons.data.eligible);
        toggleOpenFeedbackAfterInspection();
      } catch (error) {
        setIsViewButtonFeedback(false);
        Sentry.captureException(error);
        console.error;
      }
    },
    [toggleOpenFeedbackAfterInspection, userId]
  );

  useEffect(() => {
    if (canProvideFeedBack()) {
      fetchFeedBack(contract.id);
    }
  }, [canProvideFeedBack, contract.id]);

  const renderBuyerAction = () => {
    switch (contract.status) {
      case ECONTRACT_STATUS.WAITING_FOR_DELIVERY:
        return (
          <WaitingForDeliveryActions
            contract={contract}
            actorType={actorType}
            refreshContractDetails={refreshContractDetails}
          />
        );
      case ECONTRACT_STATUS.ITEM_INSPECTION:
        return (
          <>
            <WaitingForInspectionActions
              contract={contract}
              actorType={actorType}
              refreshContractDetails={refreshContractDetails}
            />
          </>
        );
      case ECONTRACT_STATUS.COMPLETED:
      case ECONTRACT_STATUS.ITEM_ACCEPTED:
        return isViewButtonFeedback ? (
          <FeedbackAndReview
            contract={contract}
            actorType={actorType}
            refreshContractDetails={refreshContractDetails}
            openFeedbackAfterInspection={openFeedbackAfterInspection}
            setOpenFeedbackAfterInspection={toggleOpenFeedbackAfterInspection}
            setIsViewButtonFeedback={closeViewButtonFeedback}
          />
        ) : (
          <></>
        );
      case ECONTRACT_STATUS.CREATED:
        return <Payment contract={contract} />;
      case ECONTRACT_STATUS.DELIVERY_ESCALATION:
        return <EscalationFollowUpActions />;
      case ECONTRACT_STATUS.INSPECTION_ESCALATION:
        return <EscalationFollowUpActions />;
      default:
        return null;
    }
  };

  return actorType === ESenderRole.PROVIDER
    ? renderSellerAction()
    : actorType === ESenderRole.CUSTOMER
    ? renderBuyerAction()
    : null;
};

export default ContractActions;
